/**
 * Firestore initialization.
 *
 * Six non-obvious choices:
 *
 *   1. Multi-tab persistence (`persistentMultipleTabManager`).
 *      The default tab manager (`persistentSingleTabManager`) only
 *      allows ONE tab to own the IndexedDB-backed cache. When a user
 *      has multiple `prest0.ai` tabs open, every tab after the first
 *      throws:
 *
 *        FirebaseError: [code=failed-precondition]: Failed to obtain
 *        exclusive access to the persistence layer. To allow shared
 *        access, multi-tab synchronization has to be enabled in all
 *        tabs.
 *
 *      The SDK then falls back to memory cache, which is fine
 *      functionally — but the failed-precondition error appears in
 *      DevTools and historically has been mistaken for a cause of
 *      stuck loading screens. Switching to
 *      `persistentMultipleTabManager` lets N tabs share the same
 *      IndexedDB without that failure.
 *
 *   2. THE OAUTH POPUP NEVER JOINS THE PERSISTENCE LEASE (2026-08-19,
 *      owner-reported production crash). The connect-app popup lands on
 *      /integrations/callback, which boots this whole SPA — so a SECOND
 *      multi-tab persistence client joined the shared IndexedDB lease,
 *      and the opener then closes the popup the instant its 3s poll sees
 *      the connection land, tearing one lease holder down mid-write.
 *      That is the classic trigger for
 *      `FIRESTORE INTERNAL ASSERTION FAILED: Unexpected state (ID:
 *      b815/ca9)`, which unmounted the entire app into the red error
 *      boundary. The popup is short-lived and renders a static landing —
 *      it needs no local cache, so it gets the in-memory default
 *      (plain getFirestore()) and never touches the lease. Detection
 *      matches the popup by its shared window name (set by
 *      connectPopupFlow's window.open) or the callback path itself.
 *
 *   3. Synchronous initialization is wrapped in try/catch and falls
 *      back to a plain `getFirestore()` if `initializeFirestore`
 *      throws (e.g. an unforeseen environment that can't open
 *      IndexedDB). The fallback Firestore still works against the
 *      network — it just won't have a local cache. Critically: this
 *      failure must NEVER bubble up to the React render tree, because
 *      every consumer of `db` imports it as a module-level default
 *      export and would crash app boot if this file threw.
 *
 *   4. ROOM IN LOCALSTORAGE BEFORE THE SDK STARTS (2026-09-28, Malaika's
 *      freezes). Multi-tab persistence writes coordination keys to
 *      localStorage on every listen and write, with no error handling: one
 *      write that hits the quota fails the SDK's queue for good (b815), and
 *      reads queued behind it never answer. The chat's transcripts filled the
 *      bin, and the old guard made room only after the chat loaded, so the SDK
 *      died again straight after every refresh. prepareStorageForFirestore()
 *      (utils/storageHeadroom.ts) now makes room first, or picks the in-memory
 *      cache when it cannot; installFirestoreStorageGuard() makes room when the
 *      bin fills later in the session. Proven with the real SDK in Chromium by
 *      scripts/probe-firestore-storage-full.mjs. Levers (build-time, absent =
 *      on): REACT_APP_FS_STORAGE_GUARD=off and REACT_APP_FS_MEMORY_FALLBACK=off.
 *
 *   5. A BROWSER THAT FROZE IN FIRESTORE'S STORAGE STARTS WITHOUT IT (Miles,
 *      2026-09-30, "only after a freeze"). The freezes left once every other
 *      fix was live (up to 36 s) all ran inside Firestore's own IndexedDB
 *      callback. On the on-device cache this page watches for one
 *      (utils/firestoreFreezeFallback.ts: a storage callback of this page's
 *      own running 3 s or more); the first one marks the browser, and its next
 *      page loads for 24 hours start on the in-memory cache. Everyone else
 *      keeps the on-device cache. Lever: REACT_APP_FS_FREEZE_FALLBACK=off.
 *
 *   6. EACH FREEZE SAYS WHICH STORAGE WORK IT WAS (2026-09-30). The health
 *      record could name Firestore's storage callback but not what it was doing
 *      (a read, a write of a large document, bookkeeping over many rows). On the
 *      on-device cache, before Firestore opens its storage, this page wraps
 *      IndexedDB's own callbacks (utils/storageCallbackWatch.ts): a callback of
 *      250 ms or more is kept with its store, its kind of request and the
 *      requests it made, and each frozen frame carries it. Measurement only.
 *      Lever: REACT_APP_STORAGE_WATCH=off.
 */
import {
  initializeFirestore,
  memoryLocalCache,
  persistentLocalCache,
  persistentMultipleTabManager,
  getFirestore,
  type Firestore,
} from 'firebase/firestore';
import app from './firebase';
import { buildEnv } from '../config/buildEnv';
import { installFirestoreStorageGuard, markFirestoreMemoryMode, prepareStorageForFirestore } from './storageHeadroom';
import { readFreezeFallback, watchForStorageFreezes } from './firestoreFreezeFallback';
import { noteEvent, setStorageCallbackLookup } from './chatHealthCounters';
import { storageCallbackDuring, watchStorageCallbacks } from './storageCallbackWatch';

// Keep in sync with CONNECT_POPUP_WINDOW_NAME in
// src/services/composio/connectPopupFlow.ts — imported literally here so
// this module depends on nothing beyond the firebase app and the leaves
// above, which import nothing (it must be safe to import first, from
// index.tsx, before any code-split chunk).
const OAUTH_POPUP_WINDOW_NAME = 'prest0n-connect';

function isOAuthPopupWindow(): boolean {
  try {
    return (
      window.name === OAUTH_POPUP_WINDOW_NAME ||
      window.location.pathname === '/integrations/callback'
    );
  } catch {
    return false;
  }
}

/** A build-time lever is on unless the build set it to exactly "off". */
function leverOn(name: string): boolean {
  return (buildEnv()[name] || '').trim().toLowerCase() !== 'off';
}

let db: Firestore;

try {
  if (isOAuthPopupWindow()) {
    // Memory-only: never a party to the multi-tab IndexedDB lease (see #2).
    db = getFirestore(app);
  } else {
    const freezeFallbackOn = leverOn('REACT_APP_FS_FREEZE_FALLBACK');
    // This browser froze in Firestore's storage within the last day (see #5).
    const frozeRecently = freezeFallbackOn && readFreezeFallback();
    const guardOn = leverOn('REACT_APP_FS_STORAGE_GUARD');
    // Room first (see #4). With the guard off this is today's boot exactly. A
    // browser that froze skips it: the in-memory cache writes nothing there.
    const prep = guardOn && !frozeRecently
      ? prepareStorageForFirestore({ memoryFallback: leverOn('REACT_APP_FS_MEMORY_FALLBACK') })
      : null;
    if (frozeRecently || (prep && prep.mode === 'memory')) {
      db = initializeFirestore(app, { localCache: memoryLocalCache() });
      // The chat's health record: this tab started without a local cache, and why.
      if (!frozeRecently) noteEvent('firestore-memory-cache');
      else noteEvent('firestore-freeze-memory-cache');
    } else {
      // Which storage work a frozen frame was doing (see #6): installed before Firestore opens its storage.
      if (leverOn('REACT_APP_STORAGE_WATCH') && watchStorageCallbacks()) setStorageCallbackLookup(storageCallbackDuring);
      db = initializeFirestore(app, {
        localCache: persistentLocalCache({
          tabManager: persistentMultipleTabManager(),
        }),
      });
      if (guardOn) installFirestoreStorageGuard();
      if (freezeFallbackOn) {
        watchForStorageFreezes(({ stored }) => {
          // A full bin refused the browser-wide mark: this tab's reloads still switch.
          if (!stored) markFirestoreMemoryMode();
          noteEvent('firestore-freeze-fallback');
        });
      }
    }
    if (prep && prep.reason !== 'room' && prep.reason !== 'storage-unavailable') {
      // eslint-disable-next-line no-console
      console.warn('[firestore] storage headroom', prep);
    }
  }
} catch (err) {
  // initializeFirestore can throw if it has already been called for
  // this app (e.g. HMR re-import), or in environments where the SDK
  // cannot open IndexedDB. In either case we want app boot to
  // continue with a working — if uncached — Firestore instance,
  // never a top-level loading-gate hang.
  // eslint-disable-next-line no-console
  console.warn(
    '[firestore] initializeFirestore failed, falling back to default getFirestore(). ' +
      'Local persistence cache is disabled for this session.',
    err,
  );
  db = getFirestore(app);
}

export default db;
